import React from 'react';
import {
    Button,
    Card,
    CardActions,
    CardContent,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AssistantActionDraft } from 'src/modules/assistant/components/AssistantActionDraft';
import { getRecommendationTiming } from 'src/modules/assistant/helpers/recommendationActions';
import {
    TActionDraft,
    TActionExecution,
    TRecommendation,
    TRecommendationOption,
} from 'src/modules/assistant/types/message';
import { TActionDraftHandlers } from 'src/modules/assistant/types/actionDraft';
import { RecommendationOptions } from './RecommendationOptions';
import { useNextBestActionCardStyles } from './styles';

const EVIDENCE_SEPARATOR = ' · ';

interface NextBestActionCardProps {
    recommendation: TRecommendation;
    execution?: TActionExecution;
    feedback?: string;
    channel?: string | null;
    showActions: boolean;
    stale: boolean;
    recalculateDisabled: boolean;
    draft?: TActionDraft;
    options: TRecommendationOption[];
    formatDateText: (value?: string) => string | undefined;
    onRecalculate: () => void;
    onSelectOption: (intent: string) => void;
    draftHandlers: TActionDraftHandlers;
}

interface MetaItemProps {
    label: string;
    value?: string;
}

const MetaItem = ({ label, value }: MetaItemProps) => {
    const classes = useNextBestActionCardStyles();
    return (
        <div className={classes.metaItem}>
            <Typography color="textSecondary" display="block" variant="caption">
                {label}
            </Typography>
            <Typography variant="subtitle2">{value}</Typography>
        </div>
    );
};

export const NextBestActionCard = ({
    recommendation,
    execution,
    feedback,
    channel,
    showActions,
    stale,
    recalculateDisabled,
    draft,
    options,
    formatDateText,
    onRecalculate,
    onSelectOption,
    draftHandlers,
}: NextBestActionCardProps) => {
    const classes = useNextBestActionCardStyles();
    const { t } = useTranslation();
    const activeDraft: TActionDraft | null =
        draft ||
        (execution
            ? { type: execution.type, summary: recommendation.title }
            : null);

    const renderActions = () => {
        if (stale)
            return (
                <>
                    <Typography color="textSecondary" variant="caption">
                        {t('Assistant.StaleRecommendation')}
                    </Typography>
                    <Button
                        color="primary"
                        disabled={recalculateDisabled}
                        onClick={onRecalculate}
                        size="small"
                        variant="contained"
                    >
                        {t('Assistant.Recalculate')}
                    </Button>
                </>
            );
        if (activeDraft)
            return (
                <AssistantActionDraft
                    action={activeDraft}
                    channel={channel}
                    execution={execution}
                    feedback={feedback}
                    {...draftHandlers}
                />
            );
        return (
            <RecommendationOptions
                channel={channel}
                onSelect={onSelectOption}
                options={options}
            />
        );
    };

    return (
        <Card className={classes.card} elevation={0}>
            <CardContent className={classes.header}>
                <Typography color="primary" variant="overline">
                    {t('Assistant.RecommendedAction')}
                </Typography>
                <Typography component="strong" display="block" variant="h6">
                    {formatDateText(recommendation.title)}
                </Typography>
            </CardContent>
            <CardContent>
                <Typography color="textSecondary" variant="overline">
                    {t('Assistant.Reason')}
                </Typography>
                <Typography variant="body2">
                    {formatDateText(recommendation.reason)}
                </Typography>
                <div className={classes.meta}>
                    {recommendation.templateTitle && (
                        <MetaItem
                            label={t('Assistant.Template')}
                            value={recommendation.templateTitle}
                        />
                    )}
                    <MetaItem
                        label={t('Assistant.Priority')}
                        value={t(
                            `Assistant.PriorityValue.${recommendation.urgency}`
                        )}
                    />
                    <MetaItem
                        label={t('Assistant.SuggestedTime')}
                        value={t(
                            `Assistant.SuggestedTimeValue.${getRecommendationTiming(
                                recommendation
                            )}`
                        )}
                    />
                </div>
                {!!recommendation.evidence?.length && (
                    <div className={classes.evidence}>
                        {recommendation.evidence.map((item) => (
                            <Typography
                                color="textSecondary"
                                display="block"
                                key={item.id}
                                variant="caption"
                            >
                                {t(`Assistant.EvidenceType.${item.type}`)}:{' '}
                                {formatDateText(item.text)}
                                {item.at &&
                                    `${EVIDENCE_SEPARATOR}${formatDateText(
                                        item.at
                                    )}`}
                            </Typography>
                        ))}
                    </div>
                )}
            </CardContent>
            {showActions && (
                <CardActions className={classes.actions}>
                    {renderActions()}
                </CardActions>
            )}
        </Card>
    );
};
